import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ShieldCheck, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import api from '../utils/api';

const statusConfig = {
  pending_review: { label: 'На проверке', class: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  processing:     { label: 'Обработка', class: 'bg-blue-500/20 text-blue-400', icon: Clock },
  approved:       { label: 'Одобрен', class: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  rejected:       { label: 'Отклонён', class: 'bg-red-500/20 text-red-400', icon: XCircle },
  incomplete:     { label: 'Не завершён', class: 'bg-gray-500/20 text-gray-400', icon: AlertTriangle },
};

const passportLabels = {
  not_uploaded: 'Не загружен',
  processing: 'Проверяется',
  success: 'Пройден',
  failed: 'Отклонён Altyn',
  rejected: 'Отклонён',
};

function getEffectiveStatus(app) {
  const passport = app.passportState;
  if (passport === 'failed' || passport === 'rejected') return 'rejected';
  return app.kycReviewStatus || app.status || 'incomplete';
}

function KycDetailModal({ app, onClose, onRefresh }) {
  const [refreshing, setRefreshing] = useState(false);
  const [result, setResult] = useState(null);

  if (!app) return null;

  const handleRefresh = async () => {
    if (!app.phone) return;
    setRefreshing(true);
    try {
      const res = await api.refreshVerification(app.phone);
      setResult({ type: 'success', text: `Статус: ${res.status || res.verification_level || JSON.stringify(res)}` });
      if (onRefresh) onRefresh();
    } catch (err) {
      setResult({ type: 'error', text: err.message });
    } finally {
      setRefreshing(false);
    }
  };

  const st = statusConfig[getEffectiveStatus(app)] || statusConfig.incomplete;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-gray-800 rounded-2xl border border-gray-700 max-w-md w-full max-h-[80vh] overflow-y-auto p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">KYC заявка</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">&times;</button>
        </div>

        <div className="space-y-3 text-sm">
          <Row label="Пользователь" value={app.fullName || app.username || '—'} />
          {app.username && <Row label="Username" value={`@${app.username}`} />}
          <Row label="User ID" value={app.userId} mono />
          <Row label="Телефон" value={app.phone || '—'} />
          <Row label="Статус KYC">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.class}`}>{st.label}</span>
          </Row>
          <Row label="Паспорт" value={passportLabels[app.passportState] || app.passportState || '—'} />
          {app.passportType && <Row label="Тип паспорта" value={app.passportType.toUpperCase()} />}
          <Row label="Уровень" value={app.identificationLevel ?? '—'} />
          {app.rejectedReason && (
            <Row label="Причина отказа">
              <span className="text-red-400">{app.rejectedReason}</span>
            </Row>
          )}
          {app.submittedAt && <Row label="Подана" value={formatDt(app.submittedAt)} />}
          {app.updatedAt && <Row label="Обновлена" value={formatDt(app.updatedAt)} />}
          {app.verifiedAt && <Row label="Верифицирован" value={formatDt(app.verifiedAt)} />}
          {app.lastActivity && <Row label="Последняя активность" value={formatDt(app.lastActivity)} />}
          {app.photoCount != null && <Row label="Загружено фото" value={app.photoCount} />}
        </div>

        {app.phone && (
          <div className="mt-5 pt-4 border-t border-gray-700">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-2 bg-purple-600/20 hover:bg-purple-600/40 text-purple-400 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              <ShieldCheck size={14} />
              {refreshing ? 'Проверка...' : 'Обновить верификацию'}
            </button>
            {result && (
              <p className={`text-xs mt-2 ${result.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                {result.text}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono, children }) {
  return (
    <div className="flex justify-between border-b border-gray-700/30 pb-1.5">
      <span className="text-gray-400">{label}</span>
      {children || (
        <span className={`text-white text-right max-w-[60%] break-all ${mono ? 'font-mono text-xs' : ''}`}>
          {value}
        </span>
      )}
    </div>
  );
}

function formatDt(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return dateStr; }
}

function formatShortDt(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return dateStr; }
}

export default function KycScreen() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getKycApplications({
        status: statusFilter || undefined,
      });
      setApplications(data.applications || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const counts = applications.reduce((acc, a) => {
    const s = getEffectiveStatus(a);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">KYC заявки</h2>
        <button
          onClick={loadData}
          disabled={loading}
          className="p-2 bg-gray-800 rounded-lg border border-gray-700 text-gray-400 hover:text-white disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Status filter */}
      <div className="overflow-x-auto mb-4 -mx-4 px-4">
        <div className="flex bg-gray-800 rounded-lg border border-gray-700/50 overflow-hidden w-max">
          {[
            { value: '', label: 'Все' },
            { value: 'pending_review', label: 'На проверке' },
            { value: 'approved', label: 'Одобренные' },
            { value: 'rejected', label: 'Отклонённые' },
            { value: 'processing', label: 'Обработка' },
            { value: 'incomplete', label: 'Не завершены' },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${
                statusFilter === f.value
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats summary */}
      {!loading && !statusFilter && Object.keys(counts).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(counts).map(([status, count]) => {
            const cfg = statusConfig[status] || statusConfig.incomplete;
            return (
              <span key={status} className={`px-2.5 py-1 rounded-lg text-xs font-medium ${cfg.class}`}>
                {cfg.label}: {count}
              </span>
            );
          })}
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Applications list */}
      <div className="space-y-2">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-gray-800 rounded-xl border border-gray-700/50 p-4">
              <div className="h-5 bg-gray-700 rounded animate-pulse w-2/3" />
            </div>
          ))
        ) : applications.length === 0 ? (
          <div className="bg-gray-800 rounded-xl border border-gray-700/50 p-8 text-center text-gray-500">
            Нет заявок
          </div>
        ) : (
          applications.map((app, i) => {
            const st = statusConfig[getEffectiveStatus(app)] || statusConfig.incomplete;
            const StIcon = st.icon;
            return (
              <button
                key={app.kycId || app.userId || i}
                onClick={() => setSelected(app)}
                className="w-full bg-gray-800 rounded-xl border border-gray-700/50 p-4 hover:bg-gray-700/30 transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <StIcon size={16} className={st.class.split(' ')[1]} />
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium truncate">
                        {app.fullName || app.username || `User ${app.userId}`}
                        {app.username && app.fullName && (
                          <span className="text-gray-400 font-normal ml-2">@{app.username}</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        {app.phone || `ID: ${app.userId}`}
                        {app.passportState && (
                          <span className="ml-2">· Паспорт: {passportLabels[app.passportState] || app.passportState}</span>
                        )}
                        {app.photoCount != null && (
                          <span className="ml-2">· {app.photoCount} фото</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${st.class}`}>
                      {st.label}
                    </span>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {formatShortDt(app.submittedAt || app.updatedAt || app.lastActivity)}
                    </span>
                  </div>
                </div>
                {app.rejectedReason && (
                  <p className="text-xs text-red-400/70 mt-1.5 truncate">
                    Причина: {app.rejectedReason}
                  </p>
                )}
              </button>
            );
          })
        )}
      </div>

      {!loading && (
        <p className="text-xs text-gray-500 mt-3">
          Всего: {applications.length} заявок
        </p>
      )}

      {selected && (
        <KycDetailModal
          app={selected}
          onClose={() => setSelected(null)}
          onRefresh={loadData}
        />
      )}
    </div>
  );
}
